import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DownloadIcon, RotateCwIcon } from "lucide-react";
import { fetcher, mutateRequest, postFetcher } from "@/lib/swr";
import type { Prd } from "@/types/domain";

// Feishu rate limits doc reads; the server already fans out image transfers
// internally (concurrency 4), so keep the client-side loop narrow.
const SYNC_CONCURRENCY = 2;
const PAGE_SIZE = 100; // server-side max page size for /prds

interface Failure {
  id: string;
  name: string;
  reason: string;
}

interface RunState {
  phase: "idle" | "running" | "done";
  done: number;
  total: number;
  synced: number;
  skipped: number;
  failures: Failure[];
  stopped: boolean;
}

const initialRun: RunState = {
  phase: "idle",
  done: 0,
  total: 0,
  synced: 0,
  skipped: 0,
  failures: [],
  stopped: false,
};

// Pull every PRD the user owns (all pages, closed included — this is a
// historical backfill) so no doc is missed by pagination.
const fetchAllOwnedPrds = async (ownerId: string): Promise<Prd[]> => {
  const all: Prd[] = [];
  let pageIndex = 0;
  for (;;) {
    const res = await fetcher<{ data?: Prd[]; total?: number }>([
      "/prds",
      { ownerId, pageIndex, pageSize: PAGE_SIZE },
    ]);
    const page: Prd[] = res?.data ?? [];
    all.push(...page);
    const total: number = res?.total ?? all.length;
    if (all.length >= total || page.length === 0) return [...new Map(all.map(p => [p.id, p])).values()];
    pageIndex += 1;
  }
};

/**
 * Self-service batch sync of the current user's Feishu-backed PRDs into the
 * DB (`content`). Client-driven loop over the existing single-PRD
 * sync-content endpoint: each request carries the user's token, so the
 * server can self-heal collaborator access on legacy docs the app cannot
 * read yet — which is exactly why the owner must be the one to trigger it.
 */
const SyncMyDocsDialog = ({ userId }: { userId: string }) => {
  const [open, setOpen] = useState(false);
  const [includeSynced, setIncludeSynced] = useState(false);
  const [run, setRun] = useState<RunState>(initialRun);
  const stopRef = useRef(false);

  const running = run.phase === "running";

  const start = async () => {
    stopRef.current = false;
    setRun({ ...initialRun, phase: "running" });
    try {
      const prds = await fetchAllOwnedPrds(userId);
      const withDoc = prds.filter((p) => p.docUrl);
      const targets = includeSynced
        ? withDoc
        : withDoc.filter((p) => !p.contentSyncedAt);
      const skipped = withDoc.length - targets.length;
      setRun((s) => ({ ...s, total: targets.length, skipped }));

      let next = 0;
      const worker = async () => {
        for (;;) {
          if (stopRef.current) return;
          const index = next;
          next += 1;
          if (index >= targets.length) return;
          const prd = targets[index];
          try {
            await postFetcher(`/prds/${prd.id}/sync-content`, { arg: {} });
            setRun((s) => ({ ...s, done: s.done + 1, synced: s.synced + 1 }));
          } catch (e) {
            const reason = e instanceof Error ? e.message : String(e);
            setRun((s) => ({
              ...s,
              done: s.done + 1,
              failures: [
                ...s.failures,
                { id: prd.id, name: prd.docName || prd.id, reason },
              ],
            }));
          }
        }
      };
      await Promise.all(
        Array.from({ length: SYNC_CONCURRENCY }, () => worker())
      );
      setRun((s) => ({ ...s, phase: "done", stopped: stopRef.current }));
      mutateRequest("/prds");
    } catch (e) {
      // Listing failed before any sync started — surface it as the summary.
      const reason = e instanceof Error ? e.message : String(e);
      setRun((s) => ({
        ...s,
        phase: "done",
        failures: [
          ...s.failures,
          { id: "#list", name: "Failed to list PRDs", reason },
        ],
      }));
    }
  };

  const onOpenChange = (value: boolean) => {
    // Keep the dialog up while the batch runs; use Stop first.
    if (!value && running) return;
    setOpen(value);
    if (value) setRun(initialRun);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs">
          <DownloadIcon className="h-3.5 w-3.5" />
          Sync my docs
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sync my docs</DialogTitle>
          <DialogDescription>
            Pull the Feishu document of every PRD you own into the database.
            Running this yourself also grants the app access to legacy docs it
            cannot read yet.
          </DialogDescription>
        </DialogHeader>

        {run.phase === "idle" && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-synced"
              checked={includeSynced}
              onCheckedChange={(v) => setIncludeSynced(v === true)}
            />
            <Label htmlFor="include-synced" className="text-sm font-normal">
              Include already synced docs (full re-sync)
            </Label>
          </div>
        )}

        {run.phase !== "idle" && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              {running && <RotateCwIcon className="h-3.5 w-3.5 animate-spin" />}
              <span>
                {running
                  ? run.total
                    ? `Syncing ${run.done} / ${run.total}…`
                    : "Listing your PRDs…"
                  : run.stopped
                  ? `Stopped at ${run.done} / ${run.total}.`
                  : "Finished."}
              </span>
            </div>
            <div className="text-muted-foreground">
              {run.synced} synced · {run.skipped} skipped ·{" "}
              {run.failures.length} failed
            </div>
            {run.failures.length > 0 && (
              <ScrollArea className="max-h-48 rounded-md border">
                <ul className="divide-y">
                  {run.failures.map((f) => (
                    <li key={f.id} className="space-y-0.5 px-3 py-2">
                      <div className="truncate font-medium">{f.name}</div>
                      <div className="text-xs text-destructive">{f.reason}</div>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            )}
          </div>
        )}

        <DialogFooter>
          {run.phase === "idle" && (
            <Button onClick={start}>
              <DownloadIcon className="mr-1.5 h-3.5 w-3.5" />
              Start
            </Button>
          )}
          {running && (
            <Button
              variant="outline"
              onClick={() => {
                stopRef.current = true;
              }}
            >
              Stop
            </Button>
          )}
          {run.phase === "done" && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyncMyDocsDialog;
